// React & Hooks

import AirDatepicker, {
  type AirDatepickerLocale,
  type AirDatepickerPosition,
} from 'air-datepicker';
// Date & Locale
import localeEn from 'air-datepicker/locale/en';
import localeId from 'air-datepicker/locale/id';
import localeKo from 'air-datepicker/locale/ko';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactTabulator } from 'react-tabulator';
// UI Components
// import ContainerCard from "@/components/ContainerCard/ContainerCard";
import ContainerCard from '@/shared/components/ui/ContainerCard/ContainerCard';
// import Filtering from "@/components/Filtering/Filtering";
import Filtering from '@/shared/components/ui/Filtering/Filtering';
// import GeneralInput from "@/components/GeneralInput/GeneralInput";
import GeneralInput from '@/shared/components/ui/GeneralInput/GeneralInput';
import { formatDateTime, getLocalISOString } from '@/shared/utils/date';
import 'air-datepicker/air-datepicker.css';

import type { Tabulator } from 'react-tabulator/lib/types/TabulatorTypes';
// Hooks & Utils
import useHistory from '@/domains/communication/hooks/useHistory';
import { URLS } from '@/shared/config/urls';

const CommunicationHistory = () => {
  const { i18n } = useTranslation();
  const tbRef = useRef<Tabulator | null>(null);
  const today = new Date();
  const [_isResetClicked, setIsResetClicked] = useState(false);

  const getYesterdayMidnight = useCallback(() => {
    const date = new Date();
    date.setDate(date.getDate());
    date.setHours(0, 0, 0, 999);
    return date;
  }, []);

  const yesterdayMidnight = getYesterdayMidnight();
  const [dateTime, setDateTime] = useState({
    start_date: formatDateTime(yesterdayMidnight),
    end_date: formatDateTime(today),
  });

  const [queryParams, setQueryParams] = useState(
    `start_time=${getLocalISOString(yesterdayMidnight)}&end_time=${getLocalISOString(today)}`
  );
  const [page, setPages] = useState(
    `start_time=${getLocalISOString(yesterdayMidnight)}&end_time=${getLocalISOString(today)}`
  );

  const { objectListData } = useHistory({
    queryParams: page,
  });

  const _data = objectListData?.data;

  useEffect(() => {
    let locale: AirDatepickerLocale = localeKo;
    if (i18n.language === 'eng') locale = localeEn;
    else if (i18n.language === 'ind') locale = localeId;

    const today = new Date();

    const yesterdayMidnight = getYesterdayMidnight();
    const optionsDate = {
      autoClose: true,
      locale: locale,
      position: 'bottom center' as unknown as AirDatepickerPosition,
      selectedDates: [yesterdayMidnight],
      timepicker: true,
      timeFormat: 'HH:mm',
      dateFormat: 'yyyy-MM-dd',
      onSelect: (date: { formattedDate: string | string[]; date: Date | Date[] }) => {
        setDateTime((prevValues) => ({
          ...prevValues,
          start_date: date.formattedDate as string,
        }));
      },
    };

    const optionsDateSecond = {
      autoClose: true,
      locale: locale,
      position: 'bottom center' as unknown as AirDatepickerPosition,
      selectedDates: [today],
      timepicker: true,
      timeFormat: 'HH:mm',
      dateFormat: 'yyyy-MM-dd',
      onSelect: (date: { formattedDate: string | string[]; date: Date | Date[] }) => {
        setDateTime((prevValues) => ({
          ...prevValues,
          end_date: date.formattedDate as string,
        }));
      },
    };

    const datepicker1 = new AirDatepicker('[name="start_date"]', optionsDate);
    const datepicker2 = new AirDatepicker('[name="end_date"]', optionsDateSecond);
    setIsResetClicked(false);

    return () => {
      datepicker1.destroy();
      datepicker2.destroy();
    };
  }, [i18n.language, getYesterdayMidnight]);

  const languageTabulator = () => {
    const translationData = localStorage.getItem('translation_kor');
    const translations = translationData ? JSON.parse(translationData) : {};
    const t = (key: string) => translations[key] || key;

    const datalanguage = {
      pagination: {
        first: t('cmn > first page'), //text for the first page button
        first_title: t('cmn > first page'), //tooltip text for the first page button
        last: t('cmn > last page'),
        last_title: t('cmn > last page'),
        prev: t('cmn > page before'),
        prev_title: t('cmn > page before'),
        next: t('cmn > next page'),
        next_title: t('cmn > next page'),
      },
    };
    return datalanguage;
  };
  const [_sortDirection, _setSortDirection] = useState('desc');

  const commonColumnProps = {
    hozAlign: 'center' as Tabulator.ColumnDefinitionAlign,
    headerHozAlign: 'center' as Tabulator.ColumnDefinitionAlign,
    resizable: false,
    widthGrow: 1,
    headerSort: false,
  };

  const communityHistoryTabulator: Tabulator.ColumnDefinition[] = [
    {
      title: 'No',
      formatter: (cell) => {
        const row = cell.getRow();
        const page = row.getTable().getPage();
        const pageSize = row.getTable().getPageSize();
        return (((page as number) - 1) * pageSize + row.getPosition(true)) as unknown as string;
      },
      width: 60,
      ...commonColumnProps,
    },
    {
      title: '수집시간',
      field: 'timestamp',
      ...commonColumnProps,
    },
    {
      title: '사이트',
      field: 'site_name',
      ...commonColumnProps,
    },
    {
      title: '접근로',
      field: 'road_name',
      ...commonColumnProps,
    },
    {
      title: '방위',
      field: 'lane_compass',
      ...commonColumnProps,
    },
    {
      title: '차선방향',
      field: 'lane_moving_direction',
      ...commonColumnProps,
    },
    {
      title: '차로',
      field: 'lane_number',
      ...commonColumnProps,
    },
    {
      title: '차종',
      field: 'vehicle_type',
      ...commonColumnProps,
    },
    {
      title: '속도(km/h)',
      field: 'speed',
      ...commonColumnProps,
    },
  ];

  const optionsTabulator = {
    pagination: true, //enable pagination
    paginationMode: 'remote',
    paginationSize: 10,
    paginationInitialPage: 1,
    rowHeight: 41,
    movableRows: false,
    index: 'id',
    locale: 'ko',
    langs: {
      ko: languageTabulator(),
    },
    resizableRows: false,
    footerElement: `<div style="padding: 0 20px 0 0; text-align: right;">총 <a id="list_count">0</a> 건</div>`,
    placeholder: "<div style='text-align:center;'> No Data</div>",
    ajaxURL: `${`${URLS.BACK_DSH}`}/object?${queryParams}`,
    ajaxConfig: {
      method: 'GET',
      credentials: 'include',
    },
    ajaxResponse: (
      _url: string,
      _params: Record<string, unknown>,
      response: { data: { total_cnt?: number; items?: unknown[]; total_pages?: number } }
    ) => {
      const totalCount = response.data.total_cnt ?? 0;

      setTimeout(() => {
        // TODO: Replace direct DOM access with React ref or state
        const footerElement = document.getElementById('list_count');
        if (footerElement) {
          footerElement.textContent = String(totalCount);
        }
      }, 0);

      return {
        data: response.data.items,
        last: response.data.total_pages,
      };
    },
    dataReceiveParams: {
      last_page: 'last',
    },
    ajaxURLGenerator: (
      url: string,
      _config: Record<string, unknown>,
      params: { page?: number; size?: number; sort: { field: string; dir: string }[] }
    ) => {
      let myUrl = url;

      const page = params.page;
      const size = params.size;
      if (page) {
        myUrl += `&page=${page}&size=${size || 10}`;
      }

      if (params.sort && params.sort.length > 0 && params.sort[0]) {
        const field = params.sort[0].field;
        const dir = params.sort[0].dir;
        myUrl += `&sort=${field}&order=${dir}`;
      }

      return myUrl;
    },
    dataLoader: false,
    sortMode: 'remote',
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setDateTime((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));
  };

  const handleReset = () => {
    setIsResetClicked(true);
    setDateTime({
      start_date: formatDateTime(yesterdayMidnight),
      end_date: formatDateTime(today),
    });
  };

  const handleSearch = useCallback(
    (value = '') => {
      const resultInput = value ? `input=${value}` : '';
      const { start_date, end_date } = dateTime;
      const dateParams: string[] = [];
      if (start_date) {
        dateParams.push(`start_time=${getLocalISOString(start_date)}`);
      }

      if (end_date) {
        dateParams.push(`end_time=${getLocalISOString(end_date)}`);
      }

      const result = [resultInput, ...dateParams].join('&');
      setQueryParams(result);
    },
    [dateTime]
  );

  useEffect(() => {
    if (tbRef.current) {
      tbRef.current.setData(`${`${URLS.BACK_DSH}`}/object?${queryParams}`);
    }
  }, [queryParams]);

  return (
    <section className="wrap">
      <div className="header-title">
        <h3>로그</h3>
        <h3>&gt;</h3>
        <h3>검지기 수집 데이터</h3>
        {/* <h3>이트 관리</h3> */}
        {/* <h3>&gt;</h3>
          <h3>횡단보도</h3> */}
      </div>

      <ContainerCard>
        <Filtering
          placeholder="사이트 / 접근로"
          disableFiltering={true}
          customWidthSelect="w-[30%]"
          onReset={handleReset}
          onSearch={handleSearch}
        >
          <div className="flex flex-row gap-2 items-center w-full">
            <GeneralInput
              isDob={true}
              type="text"
              name={'start_date'}
              onChange={handleInputChange}
              value={dateTime.start_date}
            />
            <span>-</span>
            <GeneralInput
              isDob={true}
              type="text"
              name={'end_date'}
              onChange={handleInputChange}
              value={dateTime.end_date}
            />
          </div>
        </Filtering>
      </ContainerCard>

      <ContainerCard>
        <ReactTabulator
          paginationCounter={true}
          columns={communityHistoryTabulator}
          layout={'fitColumns'}
          className="tabulator-custom w-full "
          onRef={(r) => {
            tbRef.current = r.current;
          }}
          options={optionsTabulator}
          events={{
            dataLoaded: (_data: unknown) => {
              // TODO: Replace direct DOM access with React ref or state
              const footerElement = document.getElementById('list_count');
              if (footerElement) {
                footerElement.textContent = tbRef.current?.recalc() || '';
              }
            },
            pageLoaded: (pageNumber: number) => {
              setPages(
                `start_time=${getLocalISOString(yesterdayMidnight)}&end_time=${getLocalISOString(today)}` +
                  `&page=${pageNumber}`
              );
            },
          }}
        />
      </ContainerCard>
    </section>
  );
};

export default CommunicationHistory;
